import { useState } from "react";
import { format } from "date-fns";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { cn } from "@/lib/utils";
import { useActiveConnectedTools, useLeadTools } from "../../api/useConnectedTools";
import { ToolIcon, ToolStatusPill } from "../ToolBits";
import { useToolRunner } from "./useToolRunner";
import ToolRecordBody from "./ToolRecordBody";
import type { LeadTool, ToolRecordView } from "../../types";

interface LeadContact {
  id?: number | string;
  name?: string | null;
  phone?: string | null;
  phoneCountryCode?: string | null;
}

const when = (value: string) => format(new Date(value), "d MMM yyyy, h:mm a");

function EarlierRecords({ tool, lead }: { tool: LeadTool; lead: LeadContact }) {
  const [openId, setOpenId] = useState<number | null>(null);
  const earlier: ToolRecordView[] = tool.records.slice(1);
  if (earlier.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[12px] font-medium text-[#5b6b64]">Earlier</p>
      {earlier.map((record) => {
        const open = openId === record.id;
        return (
          <div key={record.id} className="rounded-lg border border-[#e6e8e7] bg-white">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpenId(open ? null : record.id)}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[12px] text-[#385047]"
            >
              <span className="flex items-center gap-2">
                <ToolStatusPill status={record.status} />
                {when(record.createdAt)}
              </span>
              <ChevronDown className={cn("size-4 text-[#8a9a93] transition-transform", open && "rotate-180")} />
            </button>
            {open ? (
              <div className="border-t border-[#f0f2f1] p-2">
                <ToolRecordBody record={record} lead={lead} />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ToolCard({ tool, lead }: { tool: LeadTool; lead: LeadContact }) {
  const runner = useToolRunner(tool.id, lead.id);
  const latest = tool.records[0] ?? null;
  const actions = tool.actions.filter((a) => a.where.includes("tab"));

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[#e6e8e7] bg-[#fbfcfb] p-4">
      <div className="flex flex-wrap items-start gap-3">
        <ToolIcon name={tool.name} logoUrl={tool.logoUrl} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[14px] font-semibold text-[#051912]">{tool.name}</p>
            {latest ? <ToolStatusPill status={latest.status} /> : null}
          </div>
          <p className="text-[12px] text-[#5b6b64]">
            {latest
              ? `Updated ${when(latest.lastEventAt ?? latest.updatedAt)}`
              : `No ${tool.recordLabel.toLowerCase()} for this lead yet`}
          </p>
        </div>
        {actions.length ? (
          <div className="flex flex-wrap gap-2">
            {actions.map((action) => (
              <Button
                key={action.key}
                variant={latest ? "outline" : "primary"}
                size="sm"
                title={action.description ?? undefined}
                disabled={runner.isPending}
                onClick={() => runner.start(action)}
              >
                {runner.pendingKey === action.key ? "Working…" : action.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>

      {latest ? <ToolRecordBody record={latest} lead={lead} /> : null}
      {latest && !latest.status.done ? (
        <p className="text-[12px] text-[#8a9a93]">
          Not finished yet. Scores, reports and anything else the tool sends appear here as they arrive, or press
          Refresh.
        </p>
      ) : null}
      <EarlierRecords tool={tool} lead={lead} />

      {runner.dialog}
    </div>
  );
}

/**
 * A tool's tab on View Lead. `toolId` is a tool with its own tab; without it
 * this is the shared Tools tab, holding every tool that chose to sit there.
 */
export default function LeadToolsTab({ toolId }: { toolId?: number }) {
  const { lead } = useLeadForm() as { lead?: LeadContact };
  const { data, isLoading } = useLeadTools(lead?.id);

  if (!lead || isLoading) {
    return (
      <div className="flex flex-col gap-3 p-5">
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-44 w-full rounded-2xl" />
      </div>
    );
  }

  const tools = (data?.tools ?? []).filter((tool) =>
    toolId ? tool.id === toolId : tool.placement.tab === "shared",
  );
  if (tools.length === 0) {
    return <p className="p-5 text-[13px] text-[#5b6b64]">No tools are connected.</p>;
  }

  return (
    <div className="flex flex-col gap-4 p-5">
      {tools.map((tool) => (
        <ToolCard key={tool.id} tool={tool} lead={lead} />
      ))}
    </div>
  );
}

/** Overview cards for values a tool placed there, as blocks the tab can order. */
export function useLeadToolBlocks(leadId: number | string | undefined, lead: LeadContact | undefined) {
  const { data: active } = useActiveConnectedTools();
  const wanted = (active?.tools ?? []).filter((t) => t.surfaces.overview);
  const { data } = useLeadTools(leadId, wanted.length > 0);
  if (!wanted.length || !lead) return [];
  return (data?.tools ?? []).flatMap((tool) => {
    const latest = tool.records[0];
    if (!wanted.some((w) => w.id === tool.id) || !latest?.overview.length) return [];
    return [
      {
        key: `tool-${tool.id}`,
        node: (
          <div className="flex flex-col gap-2 rounded-[12px] border border-[#e6e8e7] bg-white p-3">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[13px] font-semibold text-[#051912]">{tool.name}</p>
              <ToolStatusPill status={latest.status} />
            </div>
            <ToolRecordBody record={latest} lead={lead} surface="overview" />
          </div>
        ),
      },
    ];
  });
}
